import React, { useState } from 'react';
import {
    Button,
    Progress,
    ProgressSize,
    ProgressMeasureLocation,
    ProgressVariant,
    Alert
} from '@patternfly/react-core';
import { LockIcon, EyeIcon, EyeSlashIcon } from '@patternfly/react-icons';
import { changePassword } from '@app/utils/utils';
import toast from 'react-hot-toast';
import { AppModal } from '@app/components/AppModal';
import { LogoBadge } from '@app/components/LogoBadge';
import { GradientButton } from '@app/components/GradientButton';
import { BorderedInput } from '@app/Login/BorderedInput';
import { colors, radii, fieldLabelStyle } from '@app/components/designTokens';

interface ChangePasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const ChangePasswordModal: React.FunctionComponent<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const getPasswordStrength = (pwd: string): { strength: number; label: string; variant: ProgressVariant; color: string } => {
        if (!pwd) return { strength: 0, label: '', variant: ProgressVariant.danger, color: colors.danger };

        let strength = 0;
        if (pwd.length >= 8) strength += 25;
        if (pwd.length >= 12) strength += 15;
        if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength += 20;
        if (/\d/.test(pwd)) strength += 20;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) strength += 20;

        if (strength < 40) return { strength, label: 'Weak', variant: ProgressVariant.danger, color: colors.danger };
        if (strength < 70) return { strength, label: 'Fair', variant: ProgressVariant.warning, color: colors.warning };
        return { strength, label: 'Strong', variant: ProgressVariant.success, color: colors.success };
    };

    const passwordStrength = getPasswordStrength(newPassword);
    const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
    const isFormValid = currentPassword && newPassword && confirmPassword && passwordsMatch && passwordStrength.strength >= 40;

    const resetForm = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setErrorMessage('');
        setShowCurrent(false);
        setShowNew(false);
        setShowConfirm(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = async () => {
        if (!isFormValid) return;
        setIsLoading(true);
        setErrorMessage('');
        try {
            const response = await changePassword(currentPassword, newPassword);
            if (response.ok) {
                toast.success('Password changed successfully!');
                handleClose();
            } else {
                const text = await response.text();
                setErrorMessage(text || 'Failed to change password. Please check your current password.');
            }
        } catch (error) {
            setErrorMessage('Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const eyeButton: React.CSSProperties = {
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        padding: 0,
        color: colors.placeholder,
        display: 'flex',
        alignItems: 'center'
    };

    return (
        <AppModal isOpen={isOpen} onClose={handleClose} width="md">
            <div style={{ padding: '40px 32px 32px' }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <LogoBadge size={48}>
                        <LockIcon style={{ fontSize: 24, color: colors.boltGold }} />
                    </LogoBadge>
                    <h1 style={{ color: colors.title, fontSize: '24px', fontWeight: 700, margin: 0, marginBottom: '6px', letterSpacing: '-0.5px' }}>
                        Change password
                    </h1>
                    <p style={{ color: colors.subtle, fontSize: '14px', margin: 0 }}>
                        Update your account password
                    </p>
                </div>

                {errorMessage && (
                    <Alert
                        variant="danger"
                        title={errorMessage}
                        aria-live="polite"
                        isInline
                        style={{ marginBottom: '16px' }}
                    />
                )}

                <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="current-password" style={fieldLabelStyle}>
                        Current Password<span style={{ color: colors.danger, marginLeft: 2 }}>*</span>
                    </label>
                    <BorderedInput
                        id="current-password"
                        value={currentPassword}
                        onChange={(val) => { setCurrentPassword(val); setErrorMessage(''); }}
                        type={showCurrent ? 'text' : 'password'}
                        placeholder="Enter your current password"
                        icon={<LockIcon />}
                        autoComplete="current-password"
                        rightAdornment={
                            <button type="button" onClick={() => setShowCurrent((s) => !s)} aria-label={showCurrent ? 'Hide password' : 'Show password'} style={eyeButton}>
                                {showCurrent ? <EyeSlashIcon /> : <EyeIcon />}
                            </button>
                        }
                    />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="new-password" style={fieldLabelStyle}>
                        New Password<span style={{ color: colors.danger, marginLeft: 2 }}>*</span>
                    </label>
                    <BorderedInput
                        id="new-password"
                        value={newPassword}
                        onChange={(val) => { setNewPassword(val); setErrorMessage(''); }}
                        type={showNew ? 'text' : 'password'}
                        placeholder="Create a strong password"
                        icon={<LockIcon />}
                        autoComplete="new-password"
                        rightAdornment={
                            <button type="button" onClick={() => setShowNew((s) => !s)} aria-label={showNew ? 'Hide password' : 'Show password'} style={eyeButton}>
                                {showNew ? <EyeSlashIcon /> : <EyeIcon />}
                            </button>
                        }
                    />
                    {newPassword && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '10px' }}>
                            <div style={{ flex: 1 }}>
                                <Progress
                                    value={passwordStrength.strength}
                                    size={ProgressSize.sm}
                                    measureLocation={ProgressMeasureLocation.none}
                                    variant={passwordStrength.variant}
                                    aria-label="Password strength"
                                />
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.5px', color: passwordStrength.color }}>
                                {passwordStrength.label}
                            </span>
                        </div>
                    )}
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="confirm-new-password" style={fieldLabelStyle}>
                        Confirm New Password<span style={{ color: colors.danger, marginLeft: 2 }}>*</span>
                    </label>
                    <BorderedInput
                        id="confirm-new-password"
                        value={confirmPassword}
                        onChange={(val) => { setConfirmPassword(val); setErrorMessage(''); }}
                        type={showConfirm ? 'text' : 'password'}
                        placeholder="Re-enter your new password"
                        icon={<LockIcon />}
                        autoComplete="new-password"
                        hasError={!!(confirmPassword && !passwordsMatch)}
                        rightAdornment={
                            <button type="button" onClick={() => setShowConfirm((s) => !s)} aria-label={showConfirm ? 'Hide password' : 'Show password'} style={eyeButton}>
                                {showConfirm ? <EyeSlashIcon /> : <EyeIcon />}
                            </button>
                        }
                    />
                    {confirmPassword && !passwordsMatch && (
                        <div style={{ color: colors.danger, fontSize: '13px', marginTop: '6px' }}>
                            Passwords do not match
                        </div>
                    )}
                </div>

                <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column' as const, gap: '10px' }}>
                    <GradientButton
                        onClick={handleSubmit}
                        isLoading={isLoading}
                        isDisabled={!isFormValid || isLoading}
                        isBlock
                    >
                        {isLoading ? 'Changing password...' : 'Change password'}
                    </GradientButton>
                    <Button
                        variant="link"
                        onClick={handleClose}
                        style={{ color: colors.subtle, fontWeight: 600, textAlign: 'center' as const, fontSize: '14px' }}
                        isBlock
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </AppModal>
    );
};

export { ChangePasswordModal };
